// #2: muestra dentro de la app los avisos que el admin manda (los que aún no ha visto este usuario).
// Sondea /avisos/nuevos y presenta un modal por aviso; al cerrarlo lo marca como visto.
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api, { enVisitaDelSoporte } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Ico } from './ui'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export default function AvisoAdminBanner() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [cerrados, setCerrados] = useState(() => new Set())
  const { data: avisos = [] } = useQuery({
    queryKey: ['avisos-nuevos'],
    queryFn: () => api.get('/avisos/nuevos').then(r => r.data).catch(() => []),
    enabled: !!user,
    refetchInterval: 45000,
  })
  // SOLO INTERRUMPE LO RECIENTE, Y UNA SOLA VEZ.
  //
  // Antes se abria un modal POR AVISO, uno detras de otro, con todo lo no visto de los ultimos
  // treinta dias. Abrir la app despues de unos dias era cerrar seis ventanas seguidas — y eso no se
  // lee: se cierra a ciegas, que es justo lo contrario de lo que un aviso pretende.
  //
  // Ahora: lo de las ultimas 24 h interrumpe (paso mientras no estabas y hay que enterarse); lo mas
  // viejo no abre nada y se queda en la campanita, que es donde se mira lo que uno quiere mirar.
  // Y si hay varios recientes, UN modal con todos dentro, no seis seguidos.
  //
  // LO QUE NO INTERRUMPE NO SE PIERDE. Este filtro se escribio cuando la campanita todavia no
  // existia, asi que en la practica TIRABA los avisos: lo de mas de 24 h no lo pintaba nadie, en
  // ningun sitio. Ahora vive en components/CampanaAvisos.jsx (la campana de la cabecera), y por eso
  // el modal puede permitirse callar sin que nadie se quede sin enterarse.
  const HORAS_INTERRUMPE = 24
  // t515 · la ventana sale solo de los avisos que la clínica tiene con «ventana» para esta persona (Notificaciones →
  // Configurar); los demás siguen en la campana. `ventana` ausente (un servidor de antes) = como siempre, salta.
  const salta = (a) => a.ventana !== false
  const pendientes = avisos.filter(a => !cerrados.has(a.id) && salta(a) && (a.horas == null || a.horas <= HORAS_INTERRUMPE))
  // Los que ya no interrumpen pero siguen sin leer: se dice cuantos son y donde estan, para que
  // cerrar esta ventana no parezca "ya no queda nada".
  const enLaCampana = avisos.filter(a => !cerrados.has(a.id) && (!salta(a) || (a.horas != null && a.horas > HORAS_INTERRUMPE))).length
  // t515 · Y SUENA lo que la clínica tiene con «sonido» para esta persona, una sola vez por aviso (aunque haya varias
  // pestañas: se apunta en este navegador lo que ya ha sonado).
  const sonadosRef = useRef(null)
  useEffect(() => {
    const nuevos = avisos.filter(a => a.sonido === true && (a.horas == null || a.horas <= 1))
    if (!nuevos.length || !user) return
    const clave = 'avisos_sonados:' + (user.id || 'anon')
    let sonados = sonadosRef.current
    if (!sonados) { try { sonados = new Set(JSON.parse(localStorage.getItem(clave) || '[]')) } catch { sonados = new Set() } sonadosRef.current = sonados }
    const toca = nuevos.filter(a => !sonados.has(a.id))
    if (!toca.length) return
    toca.forEach(a => sonados.add(a.id))
    try { localStorage.setItem(clave, JSON.stringify([...sonados].slice(-200))) } catch { /* sin almacenamiento: puede volver a sonar */ }
    try { const s = new Audio('/notif.mp3'); s.volume = 0.5; s.play().catch(() => {}) } catch { /* sin sonido en este navegador */ }
  }, [avisos, user])
  if (!pendientes.length) return null
  const aviso = pendientes[0]
  const resto = pendientes.slice(1)

  // Cerrar da por vistos TODOS los que se han enseñado. Es honesto porque estan todos a la vista en
  // la misma ventana: marcar solo el primero obligaria a cerrar la misma ventana varias veces, que
  // es el problema del que se venia.
  const marcarVisto = async () => {
    const ids = pendientes.map(a => a.id)
    setCerrados(s => { const n = new Set(s); ids.forEach(i => n.add(i)); return n })
    // EN UNA VISITA DEL SOPORTE, CERRAR NO ES LEER (B-504). Los avisos son de la clinica: si el soporte
    // los diera por vistos, a quien le tocan le desaparecerian sin haberlos leido nadie de alli. Se cierran
    // en esta pantalla y ya; el servidor, ademas, le contesta que no (services/soporteSoloLee).
    if (typeof enVisitaDelSoporte === 'function' && enVisitaDelSoporte()) return
    await Promise.all(ids.map(i => api.post(`/avisos/${i}/visto`).catch(() => {})))
    qc.invalidateQueries({ queryKey: ['avisos-nuevos'] })
    // Y la campanita, que comparte los mismos datos: si no se invalida, su contador sigue diciendo
    // que hay sin leer los que acaban de leerse aqui.
    qc.invalidateQueries({ queryKey: ['avisos-mios'] })
  }

  return (
    <div className="modal-safe" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={marcarVisto}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', width: '420px', maxWidth: '100%', padding: '24px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--primary)', marginBottom: '10px' }}>
          <span style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'color-mix(in srgb, var(--primary) 16%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Ico n="bell" s={20} /></span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--text-primary)' }}>{aviso.titulo}</div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{aviso.autor ? t('components.AvisoAdminBanner.de', 'De {{autor}} · ', { autor: aviso.autor }) : ''}{aviso.fecha}</div>
          </div>
        </div>
        {aviso.cuerpo && <div style={{ fontSize: '14px', lineHeight: 1.55, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', marginBottom: resto.length ? '12px' : '18px' }}>{aviso.cuerpo}</div>}
        {/* LOS DEMAS, EN LA MISMA VENTANA. Enseñarlos aqui es lo que evita las seis ventanas
            seguidas, y ademas se leen mejor juntos: "han pasado estas tres cosas". */}
        {!!resto.length && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '11px', marginBottom: '18px' }}>
            <div style={{ fontSize: '11.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--text-muted)', marginBottom: '7px' }}>
              {resto.length === 1 ? t('components.AvisoAdminBanner.y_otro_aviso', 'Y otro aviso') : t('components.AvisoAdminBanner.y_otros_avisos', 'Y otros {{length}} avisos', { length: resto.length })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', maxHeight: '190px', overflowY: 'auto' }}>
              {resto.map(a => (
                <div key={a.id} style={{ fontSize: '13px', lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 700 }}>{a.titulo}</span>
                  {a.cuerpo && <span style={{ color: 'var(--text-muted)' }}> · {a.cuerpo.length > 110 ? a.cuerpo.slice(0, 110) + '…' : a.cuerpo}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {enLaCampana > 0 && (
          <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Ico n="bell" s={13} />
            {tx('components.AvisoAdminBanner.en_la_campana_de_la', '{{x}} en la campana de la cabecera.', { x: enLaCampana === 1 ? t('components.AvisoAdminBanner.tienes_1_aviso_anterior_sin', 'Tienes 1 aviso anterior sin leer') : t('components.AvisoAdminBanner.tienes_avisos_anteriores_sin_leer', 'Tienes {{enLaCampana}} avisos anteriores sin leer', { enLaCampana }) })}
          </div>
        )}
        {/* G1: si el aviso trae url (solicitudes del portal…), botón "Abrir" que lleva a la ficha/solicitud */}
        {aviso.url ? (
          <div style={{ display: 'flex', gap: '9px' }}>
            <button onClick={marcarVisto} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>{t('components.AvisoAdminBanner.cerrar', 'Cerrar')}</button>
            <button onClick={async () => { await marcarVisto(); navigate(aviso.url) }} style={{ flex: 1.4, padding: '12px', borderRadius: '10px', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>{t('components.AvisoAdminBanner.abrir_y_atender', 'Abrir y atender')}</button>
          </div>
        ) : (
          <button onClick={marcarVisto} style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'var(--primary)', color: 'var(--sobre-primary)', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>{pendientes.length > 1 ? t('components.AvisoAdminBanner.entendido_los_he_visto', 'Entendido, los he visto') : t('components.AvisoAdminBanner.entendido', 'Entendido')}</button>
        )}
      </div>
    </div>
  )
}
